/**
 * Typology admin — edit scored-bank questions, weights, options/scores, and
 * thresholds for each typology. Saves bump the typology version on the server
 * so historical case scoring stays reproducible (cases stamp
 * typology_version_at_score at creation).
 *
 * Tree editing is intentionally not exposed here — trees are a connected graph
 * and need a different UI; for now they are read-only via the API.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { PlusIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { authFetch, getLocalApiUrl } from '../../config/api'

export default function Typologies() {
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeKey, setActiveKey] = useState(null)
  const [draft, setDraft] = useState(null) // editable copy of the active typology
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  const loadAll = useCallback(async () => {
    setLoading(true)
    setMsg(null)
    try {
      const r = await authFetch(getLocalApiUrl('/api/typologies'))
      const j = await r.json()
      if (j?.success && Array.isArray(j.data)) {
        setList(j.data)
        if (!activeKey && j.data[0]) setActiveKey(j.data[0].typology_key)
      } else {
        setMsg({ type: 'error', text: j?.error || 'Failed to load typologies' })
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Failed to load typologies' })
    } finally {
      setLoading(false)
    }
  }, [activeKey])

  useEffect(() => { loadAll() }, [loadAll])

  const active = useMemo(() => list.find((t) => t.typology_key === activeKey) || null, [list, activeKey])

  useEffect(() => {
    if (!active) { setDraft(null); return }
    // Clone so edits don't mutate the cached list.
    setDraft(JSON.parse(JSON.stringify({
      label: active.label || '',
      description: active.description || '',
      scored_bank: active.scored_bank || null,
    })))
  }, [active])

  const setBank = (updater) => setDraft((d) => ({ ...d, scored_bank: updater(d.scored_bank || {}) }))

  const save = useCallback(async () => {
    if (!activeKey || !draft) return
    setSaving(true)
    setMsg(null)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/typologies/${encodeURIComponent(activeKey)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`)
      setMsg({ type: 'success', text: `Saved — version ${j.data?.version}` })
      await loadAll()
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Save failed' })
    } finally {
      setSaving(false)
    }
  }, [activeKey, draft, loadAll])

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading typologies…</div>

  const bank = draft?.scored_bank
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Typology configuration</h1>
        <p className="text-sm text-gray-500 mt-1">Tune questions, weights, options, and thresholds. Each save increments the typology version; cases stamp the version at scoring time so historical scores stay reproducible.</p>
      </div>

      {/* Typology selector */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-3">
        {list.map((t) => (
          <button
            key={t.typology_key}
            onClick={() => setActiveKey(t.typology_key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${t.typology_key === activeKey ? 'bg-psg-teal text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {t.label}
            <span className="ml-2 text-xs opacity-70">v{t.version}</span>
          </button>
        ))}
      </div>

      {msg && (
        <div className={`mb-4 px-3 py-2 rounded text-sm ${msg.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {msg.text}
        </div>
      )}

      {!draft && <div className="text-sm text-gray-500">Select a typology to edit.</div>}

      {draft && (
        <>
          <Field label="Label">
            <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="w-full border-gray-300 rounded-md text-sm" />
          </Field>
          <Field label="Description">
            <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="w-full border-gray-300 rounded-md text-sm" rows={2} />
          </Field>

          {!bank && (
            <div className="border border-amber-200 bg-amber-50 text-amber-800 rounded-md p-3 text-sm mb-6">
              This typology has no scored bank yet. Save metadata first; tree / scored-bank seeding runs on next ensure.
            </div>
          )}

          {bank && (
            <>
              <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Thresholds</h2>
              <div className="grid grid-cols-3 gap-3 mb-6">
                <Field label="Max score">
                  <input type="number" value={bank.maxScore ?? ''} onChange={(e) => setBank((b) => ({ ...b, maxScore: Number(e.target.value) }))} className="w-full border-gray-300 rounded-md text-sm" />
                </Field>
                <Field label="Suspicious ≥">
                  <input type="number" value={bank.thresholds?.suspicious ?? ''} onChange={(e) => setBank((b) => ({ ...b, thresholds: { ...(b.thresholds || {}), suspicious: Number(e.target.value) } }))} className="w-full border-gray-300 rounded-md text-sm" />
                </Field>
                <Field label="Inconclusive ≥">
                  <input type="number" value={bank.thresholds?.inconclusive ?? ''} onChange={(e) => setBank((b) => ({ ...b, thresholds: { ...(b.thresholds || {}), inconclusive: Number(e.target.value) } }))} className="w-full border-gray-300 rounded-md text-sm" />
                </Field>
              </div>
              <Field label="Intro">
                <textarea value={bank.intro || ''} onChange={(e) => setBank((b) => ({ ...b, intro: e.target.value }))} className="w-full border-gray-300 rounded-md text-sm" rows={2} />
              </Field>

              <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Sections</h2>
              {(bank.sections || []).map((section, sIdx) => (
                <div key={sIdx} className="border border-gray-200 rounded-lg p-4 mb-4 bg-white">
                  <Field label="Section title">
                    <input value={section.title || ''} onChange={(e) => setBank((b) => updateSection(b, sIdx, { title: e.target.value }))} className="w-full border-gray-300 rounded-md text-sm" />
                  </Field>
                  {(section.questions || []).map((q, qIdx) => (
                    <div key={q.id || qIdx} className="border-t border-gray-100 pt-3 mt-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-600">{q.id}</span>
                        <span className="text-xs text-gray-500">weight</span>
                        <input type="number" value={q.weight ?? 0} onChange={(e) => setBank((b) => updateQuestion(b, sIdx, qIdx, { weight: Number(e.target.value) }))} className="w-16 border-gray-300 rounded-md text-sm" />
                      </div>
                      <textarea value={q.text || ''} onChange={(e) => setBank((b) => updateQuestion(b, sIdx, qIdx, { text: e.target.value }))} className="w-full border-gray-300 rounded-md text-sm mb-1" rows={2} />
                      {q.hint !== undefined && (
                        <input value={q.hint || ''} placeholder="Hint" onChange={(e) => setBank((b) => updateQuestion(b, sIdx, qIdx, { hint: e.target.value }))} className="w-full border-gray-300 rounded-md text-xs text-gray-600 mb-2" />
                      )}
                      <div className="space-y-1">
                        {(q.options || []).map((opt, oIdx) => (
                          <div key={oIdx} className="flex gap-2 items-center">
                            <input value={opt.label || ''} onChange={(e) => setBank((b) => updateOption(b, sIdx, qIdx, oIdx, { label: e.target.value }))} className="flex-1 border-gray-300 rounded-md text-sm" />
                            <input type="number" value={opt.score ?? 0} onChange={(e) => setBank((b) => updateOption(b, sIdx, qIdx, oIdx, { score: Number(e.target.value) }))} className="w-20 border-gray-300 rounded-md text-sm" />
                            <button onClick={() => setBank((b) => removeOption(b, sIdx, qIdx, oIdx))} className="p-1 text-gray-400 hover:text-red-500" title="Remove option">
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <button onClick={() => setBank((b) => addOption(b, sIdx, qIdx))} className="text-xs text-psg-teal hover:underline inline-flex items-center gap-1">
                          <PlusIcon className="h-3 w-3" /> add option
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}

          <div className="flex items-center gap-3 mt-6 sticky bottom-0 bg-white py-3 border-t border-gray-200">
            <button onClick={save} disabled={saving} className="px-4 py-2 bg-psg-teal text-white rounded hover:bg-psg-teal-600 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button onClick={loadAll} disabled={saving} className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded inline-flex items-center gap-2 text-sm">
              <ArrowPathIcon className="h-4 w-4" /> Reset
            </button>
            {active && (
              <span className="text-xs text-gray-500 ml-auto">version {active.version} · saved {active.updated_at ? new Date(active.updated_at).toLocaleString() : '—'}</span>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  )
}

function updateSection(bank, sIdx, patch) {
  const sections = (bank.sections || []).slice()
  sections[sIdx] = { ...sections[sIdx], ...patch }
  return { ...bank, sections }
}
function updateQuestion(bank, sIdx, qIdx, patch) {
  return updateSection(bank, sIdx, { questions: replaceAt(bank.sections[sIdx].questions, qIdx, (q) => ({ ...q, ...patch })) })
}
function updateOption(bank, sIdx, qIdx, oIdx, patch) {
  return updateSection(bank, sIdx, {
    questions: replaceAt(bank.sections[sIdx].questions, qIdx, (q) => ({
      ...q,
      options: replaceAt(q.options, oIdx, (o) => ({ ...o, ...patch })),
    })),
  })
}
function addOption(bank, sIdx, qIdx) {
  return updateSection(bank, sIdx, {
    questions: replaceAt(bank.sections[sIdx].questions, qIdx, (q) => ({
      ...q,
      options: [...(q.options || []), { label: '', score: 0 }],
    })),
  })
}
function removeOption(bank, sIdx, qIdx, oIdx) {
  return updateSection(bank, sIdx, {
    questions: replaceAt(bank.sections[sIdx].questions, qIdx, (q) => ({
      ...q,
      options: (q.options || []).filter((_, i) => i !== oIdx),
    })),
  })
}
function replaceAt(arr, idx, fn) {
  const next = (arr || []).slice()
  next[idx] = fn(next[idx])
  return next
}
